import { UserModel, OrgModel } from 'src/app/store/storeModels/user.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as io from 'socket.io-client';
import { Store } from '@ngrx/store';
import { interval, Observable, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GeneralService } from './general.service';
import { AppState } from '../store/app.state';
import { Endpoints } from '../shared/config/endpoints';

export type AgentAvailabilityStatus = 'online' | 'away' | 'busy' | 'offline';
export type AgentStatusMode = 'manual' | 'automatic';

export interface AgentStatusHistoryItem {
  id?: string;
  agentId: string | number;
  agentName: string;
  agentEmail?: string;
  previousStatus: AgentAvailabilityStatus;
  newStatus: AgentAvailabilityStatus;
  timestamp: string | Date;
  source: 'manual' | 'automatic';
  orgId: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  socket: any;
  readonly socketUrl: string = environment.chatServiceUrl;
  private inactivityTimeout: any;
  private readonly INACTIVITY_LIMIT = 180000;
  private readonly HEARTBEAT_INTERVAL = 10000;
  private readonly HEARTBEAT_THRESHOLD = 35000;
  private readonly HIDDEN_DISCONNECT_DELAY = 2220000;
  private lastActivityTime: number = Date.now();
  private api = this.endpoint.chatServiceUrl;
  private orgId: string;
  private org: OrgModel;
  private user: UserModel;
  public userIsAgent = false;
  public heartbeatInterval: any;
  public lastHeartbeatTimestamp: number;
  public heartbeatMonitorInterval: any;
  private userUniqueId = '';
  public isAgentIdle: boolean;
  public channel = new BroadcastChannel('app-activity');
  public isLeader: boolean;
  public isListenerInit = false;
  public debouncedResetInactivity: () => void;
  
  public currentAgentStatus: AgentAvailabilityStatus = 'online';
  public currentAgentStatus$ = new BehaviorSubject<AgentAvailabilityStatus>('online');
  public agentStatusMode: AgentStatusMode = 'manual';
  public agentStatusMode$ = new BehaviorSubject<AgentStatusMode>('manual');
  public statusHistory$ = new BehaviorSubject<AgentStatusHistoryItem[]>([]);

  constructor(
    private http: HttpClient,
    private endpoint: Endpoints,
    store: Store<AppState>,
    private gs: GeneralService) {
    store.select('userInfo').subscribe(info => {
      this.orgId = (info.organization ? info.organization.id : '') + '';
      this.org = info.organization;
      this.user = info.user;
      this.userUniqueId = (this.user ? this.user.id : '') + '-' + this.orgId;
      this.userIsAgent = !!info.isAgent;

      this.initStatusSettings();

      if (this.user && this.org && !this.socket) {
        const socketQuery = { userId: this.user.id, orgId: this.org.id, uniqueId: this.userUniqueId };
        this.socket = io(this.socketUrl, { query: socketQuery });
        this.setupSocketListeners();
      }
    });

    this.debouncedResetInactivity = this.debounce(this.resetInactivityTimer.bind(this), 300);
  }

  private setupSocketListeners() {
    this.socket.on('connect', () => {
      if (!this.userIsAgent) return;
      this.joinOnlineAgents();
      this.getOnlineAgent().subscribe();
      
      if (this.agentStatusMode === 'manual') {
        this.setAgentStatus(this.currentAgentStatus, 'manual', true);
      } else {
        this.setAgentStatus('online', 'automatic', true);
        this.setupInactivityListener();
        this.startHeartbeat();
      }
      this.initAgentSession();
    });
    this.setupBrowserCloseListener();

    this.socket.on('disconnect', () => {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        clearInterval(this.heartbeatMonitorInterval);
      }
      if (this.inactivityTimeout) {
        clearTimeout(this.inactivityTimeout);
      }
    });

    this.socket.on('reconnect', () => {
      console.log('socket reconnected');
      if (!this.userIsAgent) return;
      this.joinOnlineAgents();
      this.getOnlineAgent().subscribe();
      if (this.isLeader) {
        if (this.agentStatusMode === 'manual') {
          this.setAgentStatus(this.currentAgentStatus, 'manual', true);
        } else {
          this.setAgentStatus('online', 'automatic', true);
          this.startHeartbeat();
          this.setupInactivityListener();
        }
      }
    });

    this.socket.on('agent_status_updated', (data: any) => {
      if (data && this.user && String(data.userId || data.agentId) === String(this.user.id)) {
        if (data.status && data.status !== this.currentAgentStatus) {
          this.currentAgentStatus = data.status;
          this.currentAgentStatus$.next(data.status);
          this.isAgentIdle = (data.status === 'away' || data.status === 'busy' || data.status === 'offline');
        }
      }
    });
  }

  public setupInactivityListener() {
    if (this.isListenerInit) return;
    this.isListenerInit = true;
    this.resetInactivityTimer();
    window.addEventListener('mousemove', this.debouncedResetInactivity);
    window.addEventListener('keydown', this.debouncedResetInactivity);
    window.addEventListener('scroll', this.debouncedResetInactivity);
    window.addEventListener('touchstart', this.debouncedResetInactivity);
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  public setupRemoveListener() {
    if (!this.isListenerInit) return;
    this.isListenerInit = false;
    clearTimeout(this.inactivityTimeout);
    window.removeEventListener('mousemove', this.debouncedResetInactivity);
    window.removeEventListener('keydown', this.debouncedResetInactivity);
    window.removeEventListener('scroll', this.debouncedResetInactivity);
    window.removeEventListener('touchstart', this.debouncedResetInactivity);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  public resetInactivityTimer() {
    if (!this.isLeader || !this.userIsAgent) return;
    if (this.agentStatusMode === 'manual') return; // In manual mode, user activity does not override manual status

    this.lastActivityTime = Date.now();
    clearTimeout(this.inactivityTimeout);

    if (this.isAgentIdle || this.currentAgentStatus === 'away') {
      this.setAgentStatus('online', 'automatic');
    }

    this.inactivityTimeout = setTimeout(() => {
      if (!this.isLeader || document.hidden || this.agentStatusMode === 'manual') return;
      const elapsed = Date.now() - this.lastActivityTime;
      if (elapsed >= this.INACTIVITY_LIMIT) {
        this.setAgentStatus('away', 'automatic');
      } else {
        this.inactivityTimeout = setTimeout(() => {
          if (this.isLeader && !document.hidden && this.agentStatusMode === 'automatic') {
            this.setAgentStatus('away', 'automatic');
          }
        }, this.INACTIVITY_LIMIT - elapsed);
      }
    }, this.INACTIVITY_LIMIT);
  }

  public startHeartbeatMonitor() {
    this.lastHeartbeatTimestamp = Date.now();
    this.heartbeatMonitorInterval = setInterval(() => {

    }, 5000);
  }

  public startHeartbeat() {
    clearInterval(this.heartbeatInterval);
    clearInterval(this.heartbeatMonitorInterval);
    this.heartbeatInterval = setInterval(() => {
      if (!this.isLeader) return;

      this.lastHeartbeatTimestamp = Date.now();

      if (this.agentStatusMode === 'manual') {
        // In manual mode, heartbeat syncs current agent status
        this.emit('agent_heartbeat', {
          org_id: this.orgId,
          userId: this.user ? this.user.id : null,
          status: this.currentAgentStatus
        }).subscribe();
      } else {
        if (document.hidden || this.isAgentIdle) {
          this.setAgentStatus('away', 'automatic');
        } else {
          this.setAgentStatus('online', 'automatic');
        }
      }
    }, 10000);
    this.startHeartbeatMonitor();
  }

  public setupBrowserCloseListener() {
    window.addEventListener('beforeunload', () => {
      if (this.isLeader) {
        this.channel.postMessage('leader-gone');
      }
    });

    this.channel.onmessage = (event) => {
      if (event.data === 'leader-gone') {
        this.claimLeadership();
      } else if (event.data === 'current-leader') {
      }
    };
  }

  public handleVisibilityChange() {
    if (!this.isLeader || !this.userIsAgent) return;
    if (this.agentStatusMode === 'manual') return; // In manual mode, tab hiding does not force away

    if (document.hidden) {
      this.setAgentStatus('away', 'automatic');
      setTimeout(() => {
        if (document.hidden && this.isLeader && this.agentStatusMode === 'automatic') {
          this.setAgentStatus('away', 'automatic');
        }
      }, this.HIDDEN_DISCONNECT_DELAY);
    } else {
      if (this.isAgentIdle || this.currentAgentStatus === 'away') {
        this.setAgentStatus('online', 'automatic');
        this.getOnlineAgent().subscribe();
        this.getOnlineAgents();
      }
    }
  }


  public claimLeadership() {
    this.channel.postMessage('current-leader');
    if (!this.isLeader) {
      this.becomeLeader()
      this.setAgentOnline();
    }
  }

  public becomeLeader() {
    this.isLeader = true;
    this.setupInactivityListener();
    this.startHeartbeat();
  }

  public resignLeadership() {
    this.isLeader = false;
    this.setupRemoveListener();
    clearInterval(this.heartbeatInterval);
    clearInterval(this.heartbeatMonitorInterval);
    clearTimeout(this.inactivityTimeout);
  }

  initAgentSession() {
    this.isLeader = true;
    if (!this.userIsAgent) return;
    this.isAgentIdle = false;
    this.getOnlineAgent().subscribe();
    this.setAgentOnline();
  }

  public debounce(func: Function, wait: number) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }





  listen(eventName: string): Observable<any> {
    return new Observable(subscriber => {
      if (this.socket) {
        this.socket.on(eventName, data => {
          subscriber.next(data);
        });
      }
    });
  }

  emit(eventName: string, data: any): Observable<void> {
    return new Observable(subscriber => {
      if (this.socket) {
        this.socket.emit(eventName, data, () => {
          subscriber.next();
        });
      } else {
        subscriber.next();
      }
    });
  }

  joinChat() {
    if (!this.socket || !this.user) return;
    return this.socket.emit('User Joined-Chat', {
      id: this.user.id + this.orgId,
      org: this.orgId
    })
  }

  joinAgentNofication() {
    return this.emit('join_agent_notification', {
      uniqueId: this.userUniqueId,
      orgId: this.orgId
    });

  }
  joinOnlineAgents() {
    if (this.userIsAgent) {
      this.emit('join_online_agents', {
        user: {
          ...this.user,
          uniqueId: this.userUniqueId,
        },
        organization: this.org
      }).subscribe()
    }
  }
  disconnect_online_agent() {
    this.emit('disconnect_online_agent', {
      org_id: this.orgId,
      userId: this.user ? this.user.id : null,
      status: 'offline'
    }).subscribe();
  }

  setAgentStatus(status: AgentAvailabilityStatus, source: 'manual' | 'automatic' = 'manual', isInitial = false) {
    if (!this.userIsAgent) return;

    // In manual mode, reject automatic status overrides unless it's initial connection setup
    if (this.agentStatusMode === 'manual' && source === 'automatic' && !isInitial) {
      return;
    }

    const previousStatus = this.currentAgentStatus || 'offline';
    this.currentAgentStatus = status;
    this.currentAgentStatus$.next(status);

    this.isAgentIdle = (status === 'away' || status === 'busy' || status === 'offline');

    if (this.user && typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem('notch_agent_status_' + this.user.id, status);
      } catch (e) {}
    }

    // Record status history if status actually changed or explicitly requested
    if (!isInitial || previousStatus !== status) {
      this.recordStatusHistory(previousStatus, status, source);
    }

    const payload = {
      org_id: this.orgId,
      userId: this.user ? this.user.id : null,
      agentId: this.user ? this.user.id : null,
      status: status,
      previousStatus: previousStatus,
      source: source,
      timestamp: new Date().toISOString()
    };

    // Generic status broadcast for backend routing, SLA, and analytics
    this.emit('change_agent_status', payload).subscribe();

    // Map to specific socket events for compatibility with backend listeners
    if (status === 'online') {
      this.emit('set_agent_online', payload).subscribe();
    } else if (status === 'away') {
      this.emit('set_agent_idle', payload).subscribe();
      this.emit('set_agent_away', payload).subscribe();
    } else if (status === 'busy') {
      this.emit('set_agent_busy', payload).subscribe();
    } else if (status === 'offline') {
      this.emit('set_agent_offline', payload).subscribe();
      this.disconnect_online_agent();
    }
  }

  setAgentStatusMode(mode: AgentStatusMode) {
    this.agentStatusMode = mode;
    this.agentStatusMode$.next(mode);

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem('notch_agent_status_mode_' + this.orgId, mode);
      } catch (e) {}
    }

    this.emit('set_agent_status_mode', {
      org_id: this.orgId,
      mode: mode,
      updatedBy: this.user ? this.user.id : null,
      timestamp: new Date().toISOString()
    }).subscribe();

    if (mode === 'automatic') {
      this.setupInactivityListener();
      this.startHeartbeat();
    } else {
      if (this.inactivityTimeout) {
        clearTimeout(this.inactivityTimeout);
      }
      this.setAgentStatus(this.currentAgentStatus, 'manual');
    }
  }

  private initStatusSettings() {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const storedMode = localStorage.getItem('notch_agent_status_mode_' + this.orgId) as AgentStatusMode;
        if (storedMode === 'manual' || storedMode === 'automatic') {
          this.agentStatusMode = storedMode;
          this.agentStatusMode$.next(storedMode);
        } else {
          this.agentStatusMode = 'manual';
          this.agentStatusMode$.next('manual');
        }

        const storedStatus = localStorage.getItem('notch_agent_status_' + (this.user ? this.user.id : '')) as AgentAvailabilityStatus;
        if (storedStatus && ['online', 'away', 'busy', 'offline'].includes(storedStatus)) {
          this.currentAgentStatus = storedStatus;
          this.currentAgentStatus$.next(storedStatus);
        } else {
          this.currentAgentStatus = 'online';
          this.currentAgentStatus$.next('online');
        }

        const rawHistory = localStorage.getItem('notch_agent_status_history_' + this.orgId);
        if (rawHistory) {
          const parsed = JSON.parse(rawHistory);
          if (Array.isArray(parsed)) {
            this.statusHistory$.next(parsed);
          }
        }
      } catch (e) {
        console.error('Error loading agent status settings:', e);
      }
    }
  }

  private recordStatusHistory(previousStatus: AgentAvailabilityStatus, newStatus: AgentAvailabilityStatus, source: 'manual' | 'automatic') {
    if (!this.user) return;
    const historyItem: AgentStatusHistoryItem = {
      id: Date.now().toString() + '-' + Math.random().toString(36).substring(2, 6),
      agentId: this.user.id,
      agentName: `${this.user.firstName || ''} ${this.user.lastName || ''}`.trim() || 'Agent',
      agentEmail: this.user.email,
      previousStatus: previousStatus,
      newStatus: newStatus,
      timestamp: new Date().toISOString(),
      source: source,
      orgId: this.orgId
    };

    const currentHistory = this.statusHistory$.getValue() || [];
    const updatedHistory = [historyItem, ...currentHistory].slice(0, 200);
    this.statusHistory$.next(updatedHistory);

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem('notch_agent_status_history_' + this.orgId, JSON.stringify(updatedHistory));
      } catch (e) {}
    }
  }

  setAgentIdle() {
    this.setAgentStatus('away', this.agentStatusMode === 'automatic' ? 'automatic' : 'manual');
  }

  setAgentOnline() {
    this.setAgentStatus('online', this.agentStatusMode === 'automatic' ? 'automatic' : 'manual');
  }

  isAgentAvailable(agentId: string | number, onlineAgentsList?: any[]): boolean {
    if (!agentId) return false;
    // Check against current user if self
    if (this.user && String(this.user.id) === String(agentId)) {
      return this.currentAgentStatus === 'online';
    }
    // Check against onlineAgents socket list
    if (onlineAgentsList && Array.isArray(onlineAgentsList)) {
      const found = onlineAgentsList.find(item => item && item.agent && String(item.agent.id) === String(agentId));
      if (!found) return false;
      const status = (found.status || (found.isIdle ? 'away' : 'online')).toLowerCase();
      return status === 'online';
    }
    return false;
  }

  getAgentAvailabilityStatus(agentId: string | number, onlineAgentsList?: any[]): AgentAvailabilityStatus {
    if (!agentId) return 'offline';
    if (this.user && String(this.user.id) === String(agentId)) {
      return this.currentAgentStatus;
    }
    if (onlineAgentsList && Array.isArray(onlineAgentsList)) {
      const found = onlineAgentsList.find(item => item && item.agent && String(item.agent.id) === String(agentId));
      if (!found) return 'offline';
      if (found.status && ['online', 'away', 'busy', 'offline'].includes(found.status.toLowerCase())) {
        return found.status.toLowerCase() as AgentAvailabilityStatus;
      }
      return found.isIdle ? 'away' : 'online';
    }
    return 'offline';
  }
  getOnlineAgents() {
    return this.listen('online_agents');
  }

  getOnlineAgent() {
    return this.http.get(`${this.api}/availability/all-agents/${this.orgId}`);
  }

  autoAssignTicket(payload) {
    return this.http.post(`${environment.chatServiceUrl}/ticket/auto-assign`, payload);
  }


  getAgentIncomingNotify() {
    return this.listen('agent_incoming_notification');
  }
  notifyAgents(payload: { agentList: any[], ticketCode: string, message: string }, internalGroupUsers: any[], user: { id, firstName, lastName, orgName }) {
    let body = null;
    if (internalGroupUsers && internalGroupUsers.length > 0) {
      const internalIDs = internalGroupUsers.map(internal => internal.id);
      const agents = payload.agentList.filter(agent => !internalIDs.includes(agent.userId));
      body = {
        creatorId: user.id,
        creatorName: `${user.firstName} ${user.lastName}`,
        orgName: user.orgName,//: this.org.name
        ticketCode: payload.ticketCode,
        agentList: agents,
        message: payload.message,
        ticketPageLink: `${environment.notchFrontendServiceUrl}/ticket/ticket-view/${payload.ticketCode}`,
        orgTicketEmail: 'noreply@notchcx.io',
        date: new Date(),
        service: 'tickets',
        subject: `@${user.firstName} ${user.lastName} mentioned you in Tickets`,
        template: 'mentioned you'
      }
      //this one sends to agents
      this.notifyAgentSocket(body);
      this.notifyAgentsEmail(body);
      //this has to send personalized messages to internal response persons
      const internalGroup = payload.agentList.filter(agent => internalIDs.includes(agent.userId));
      let response = null;
      for (let i = 0; i < internalGroup.length; i++) {
        const person = internalGroup[i];
        const { userId, name } = person;
        body.ticketPageLink = `${environment.notchFrontendServiceUrl}/internal-response/${payload.ticketCode}/${this.gs.org.id}/${userId}/${name}/${this.gs.org.name}`;
        body.agentList = [person];
        this.notifyAgentSocket(body);
        response = this.notifyAgentsEmail(body);
      }

      return response;
    } else {
      body = {
        creatorId: user.id,
        creatorName: `${user.firstName} ${user.lastName}`,
        orgName: user.orgName,
        ticketCode: payload.ticketCode,
        agentList: payload.agentList,
        message: payload.message,
        ticketPageLink: `${environment.notchFrontendServiceUrl}/ticket/ticket-view/${payload.ticketCode}`,
        orgTicketEmail: 'noreply@notchcx.io',//this.gs.orgEmailTicket,
        date: new Date(),
        service: 'tickets',
        subject: `@${user.firstName} ${user.lastName} mentioned you in Tickets`,
        template: 'mentioned you'
      }
      this.notifyAgentSocket(body);
      return this.notifyAgentsEmail(body);
    }

  }
  notifyAgentSocket(payload: { agentList: any[] }) {
    payload.agentList.forEach(agent => {
      this.emit('notify_agent_socket', {
        ...payload,
        agent: agent
      }).subscribe(() => {
      })
    })
  }

  saveAgentNotify(payload) {
    const body = {
      orgName: this.org.name,
      event: 'AGENT_MENTION',
      creatorId: this.user.id,
      creatorName: `${this.user.firstName} ${this.user.lastName}`,
      creatorTeams: [],
      itemId: payload.ticketCode,
      orgId: this.org.id,
      service: 'tickets',
      userIds: payload.userIds,
      subject: `@${this.user.firstName} ${this.user.lastName} mentioned you in Tickets`,
      template: 'mentioned you',
      email_list: payload.email_list
    }
    return this.http.post(environment.notificationsServiceUrl + '/notifications/action/user', body)
  }

  notifyAgentsEmail(payload) {
    const body = {
      ...payload,
      agentList: payload.agentList.map(agent => agent.email)
    }
    return this.emit('notify_agents_email', body);
  }

  saveIntegrations(payload) {
    return this.http.post(`${this.api}/integrations/${this.org.id}/`, payload);
  }

  getIntegrations(query: any) {
    return this.http.get(`${this.api}/integrations/${this.org.id}`, {
      params: query
    });
  }
  deleteIntegrations(payload: any) {
    return this.http.delete(`${this.api}/integrations/${this.org.id}`, {
      params: payload
    });
  }

  getWhatsappNumber(params: any) {
    return this.http.get(`${this.api}/bots/whatsapp/meta/get-numbers`, {
      params
    });
  }

  requestOtp(payload: any) {
    return this.http.post(`${this.api}/bots/whatsapp/meta/request-otp`, payload);
  }

  verifyNumber(payload: any) {
    return this.http.post(`${this.api}/bots/whatsapp/meta/verify-number`, payload);
  }
  subscribeToNumber(payload: any) {
    return this.http.post(`${this.api}/bots/whatsapp/meta/register-phone-numbers`, payload);
  }
  unsubscribeToNumber(payload: any) {
    return this.http.post(`${this.api}/bots/whatsapp/meta/deregister-phone-numbers`, payload);
  }

  ngOnDestroy() {
    this.setupRemoveListener();
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.heartbeatMonitorInterval) {
      clearInterval(this.heartbeatMonitorInterval);
    }
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }

    if (this.channel) {
      this.channel.close();
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  }


}